import { useState } from 'react';
import { useNotificationPrefs, type BrowserPermission } from '../data/notifications';
import { usePushSubscription } from '../data/push';
import { deniedHelp, isIos, isStandalone } from '../lib/notify-platform';
import { Icon } from './Icon';

/**
 * Az ertesitesi engedely bekerese.
 *
 * Volt egy csendes ellentmondas a rendszerben: a felhasznalo beallitotta,
 * hogy kerjen ertesitest, a bongeszo viszont soha nem kapta meg ra az
 * engedelyt - es senki nem szolt. Az illeto varta az ertesitest, ami nem jott.
 *
 * ─── Az engedelyt CSAK kattintasra kerjuk ───────────────────────────────────
 *
 * Korabban betolteskor is megprobaltuk, magatol - a Chromium ugyanis felhozza
 * a kerdest anelkul is, es igy megsporoltunk egy kattintast. Ez iPhone-on
 * KOMOLY KART okozott: a Safari a felhasznaloi mozdulat nelkuli kerest nem
 * teljesiti, a PWA-nak viszont ez az EGYETLEN alkalma. Aki igy jart, utana
 * sehol nem tudta engedelyezni: a kezdokepernyos alkalmazas meg sem jelenik az
 * iOS ertesitesi beallitasai kozott, amig egyszer engedelyt nem kapott.
 *
 * Egy megsporolt kattintas nem er annyit, hogy egy egesz keszuleken
 * visszafordithatatlanul elveszitsuk az ertesiteseket. Ezert most minden
 * platformon a gomb inditja a kerest.
 *
 * ─── Egy gomb, ket lepes ────────────────────────────────────────────────────
 *
 * Az engedely magaban nem eleg: zart alkalmazasnal az ertesites WEB PUSH-on
 * erkezik, ahhoz pedig feliratkozas is kell. Korabban az engedelyt a sav
 * kerte, a feliratkozast viszont a Beallitasokban kellett kulon bekapcsolni -
 * aki csak a savot latta, engedelyt adott, es tovabbra sem kapott semmit.
 */
export function NotificationNudge({
  permission,
  onRequest,
}: {
  permission: BrowserPermission;
  onRequest: () => Promise<void> | void;
}) {
  const { data: prefs } = useNotificationPrefs();
  const push = usePushSubscription();
  const [dismissed, setDismissed] = useState(false);
  const [busy, setBusy] = useState(false);

  const wanted = prefs ? prefs.globalLevel !== 'none' : false;

  if (!wanted || dismissed) return null;

  /*
    iOS-en a Notification API CSAK a kezdokepernyore tett alkalmazasban letezik.
    Safari-fulon nincs mit engedelyezni - ott nem gombot kell mutatni, hanem
    megmondani, hogy eloszor ki kell tenni a kezdokepernyore.
  */
  if (iosNeedsInstall(permission)) {
    return (
      <Bar>
        <p className="min-w-0 flex-1 text-ink-muted">
          Kértél értesítéseket. iPhone-on ehhez az AVSlacket a kezdőképernyőre kell tenni: a
          Safariban <strong>Megosztás → Hozzáadás a főképernyőhöz</strong>, és onnan megnyitni.
        </p>
        <Hide onHide={() => setDismissed(true)} />
      </Bar>
    );
  }

  if (permission === 'granted' || permission === 'unsupported') return null;

  /*
    Az ELUTASITAST nem lehet parbeszedbol visszavonni: ha egyszer nemet
    mondtak, a bongeszo tobbe nem kerdez ra. Ilyenkor nem gombot kinalunk -
    az semmit nem tenne -, hanem megmondjuk, hol lehet visszaallitani. Ez
    platformonkent MAS: egy kezdokepernyos alkalmazasban nincs cimsor es nincs
    lakat ikon, tehat a bongeszos tanacs ott csak osszezavar.
  */
  if (permission === 'denied') {
    return (
      <Bar>
        <p className="min-w-0 flex-1 text-ink-muted">
          Kértél értesítéseket, de a böngésző korábban elutasította. {deniedHelp()}
        </p>
        <Hide onHide={() => setDismissed(true)} />
      </Bar>
    );
  }

  /*
    A gomb EGY mozdulatbol intezi az engedelyt ES a feliratkozast.

    A `push.enable()` maga is az engedellyel kezd, es a valaszt meg ebben a
    kattintasban kapja meg - a bongeszo szamara tehat vegig "felhasznaloi
    mozdulat" marad. Ahol nincs push (nincs VAPID-kulcs, vagy asztali app),
    ott marad a puszta engedelykeres.
  */
  const allow = async () => {
    setBusy(true);
    try {
      if (push.state === 'unsupported' || push.state === 'unavailable') {
        await onRequest();
      } else {
        await push.enable();
        await onRequest();
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <Bar>
      <p className="min-w-0 flex-1 text-ink-muted">
        Kértél értesítéseket, de a böngésző még nem adott rá engedélyt.
      </p>
      <button
        type="button"
        disabled={busy}
        onClick={() => void allow()}
        className="shrink-0 rounded-control bg-accent px-3 py-1 text-xs font-semibold text-accent-ink transition-colors duration-(--duration-quick) hover:bg-accent-hover disabled:opacity-50"
      >
        {busy ? 'Kérés…' : 'Engedélyezés'}
      </button>
      <Hide onHide={() => setDismissed(true)} />
    </Bar>
  );
}

const Bar = ({ children }: { children: React.ReactNode }) => (
  <div className="flex items-center gap-3 border-b border-border-soft bg-accent-soft/50 px-4 py-2 text-sm">
    <span aria-hidden="true" className="shrink-0 text-accent">
      <Icon name="ertesites" />
    </span>
    {children}
  </div>
);

const Hide = ({ onHide }: { onHide: () => void }) => (
  <button
    type="button"
    onClick={onHide}
    title="Elrejtés"
    aria-label="Elrejtés"
    className="grid size-7 shrink-0 place-items-center rounded-control text-ink-faint transition-colors duration-(--duration-quick) hover:bg-surface-hover hover:text-ink"
  >
    <Icon name="bezaras" size="sm" />
  </button>
);

/**
 * iOS-en Safari-fulon vagyunk, ahol nincs mit engedelyezni.
 *
 * A `unsupported` itt nem hiba, hanem allapot: az iOS a Notification API-t
 * csak a kezdokepernyos alkalmazasnak adja meg.
 */
const iosNeedsInstall = (permission: BrowserPermission): boolean =>
  permission === 'unsupported' && isIos() && !isStandalone();
